import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../layout/header";
import {
  createTecgymAdminRoleURL,
  createTecgymAdminUserURL,
  deleteTecgymAdminRoleURL,
  deleteTecgymAdminUserURL,
  getTecgymAdminRolesURL,
  getLocalServerURL,
  getTecgymAdminUsersURL,
  updateTecgymAdminRoleURL,
  updateTecgymAdminUserURL
} from "../libraries/apiUrl";
import {
  ADMIN_PROFILE_PHOTO_UPDATED_EVENT,
  BRANCH_TABS,
  getAdminProfilePicture,
  getAdminUser,
  isLoggedInAdmin,
  matchesLoggedInAdminUser,
  saveAdminProfilePicture
} from "../libraries/auth";
import { copyToClipboard, showToast } from "../libraries/clipboardUtils";
import {
  buildActivityChangeValues,
  buildAppActivityFields,
  withAppActivityFields
} from "../libraries/activityLogger";
import { canAccessNavRoute, canViewEmployeeManagementTab, getDefaultAccessibleRoute } from "../libraries/privileges";
import { _delete, _get, _post, _put } from "../libraries/fetch";
import ApplyRequestPanel from "../features/employee/ApplyRequestPanel";
import AttendanceTab from "../features/employee/AttendanceTab";
import DailyAttendanceTab from "../features/employee/DailyAttendanceTab";
import { getCurrentEmployee, getEmployeeName } from "../libraries/tecgymAdminAttendance";
import { buildUserActivityLists, fetchTecgymAdminUsers } from "../libraries/tecgymAdminUsers";

const tabs = [
  { id: "users", label: "User" },
  { id: "roles", label: "Role" },
  { id: "attendance", label: "Attendance" },
  { id: "daily-attendance", label: "Daily Attendance" },
  { id: "apply-leave", label: "Apply Leave" },
  { id: "apply-wfh", label: "Apply Work From Home" }
];

const pageHeadings = {
  roles: "Manage Roles",
  attendance: "Attendance",
  "daily-attendance": "Daily Attendance",
  "apply-leave": "Apply Leave",
  "apply-wfh": "Apply Work From Home",
  "leave-requests": "Leave Requests",
  "wfh-requests": "Work From Home Requests"
};

const pageSubtitles = {
  roles: "Choose User to view all employees or Role to view available roles.",
  attendance: "Mark attendance and view the full month register with check-in, check-out, working hours, and deficit hours.",
  "daily-attendance": "View every employee's attendance for the selected day, in alphabetical order, and download it as a PNG image.",
  "apply-leave": "Use Apply to request leave, or Leave Status to review requests on this screen.",
  "apply-wfh": "Use Apply to request work from home, or Work From Home Status to review requests on this screen.",
  "leave-requests": "Other employees' leave requests, shown as Approved or Rejected.",
  "wfh-requests": "Other employees' work from home requests, shown as Approved or Rejected."
};

function requestViewerRole(name) {
  const key = String(name || "").toLowerCase().replace(/[^a-z]/g, "");

  if (key.includes("rashid") && key.includes("ebad")) {
    return "rashid";
  }

  if (key.includes("masood") || key.includes("shanzay")) {
    return "approver";
  }

  return "employee";
}

const ROLE_SECTION_ORDER = [
  { key: "admin", label: "Admin", aliases: ["admin", "administrator"] },
  { key: "developer", label: "Developer", aliases: ["developer"] },
  { key: "business", label: "Business", aliases: ["business", "bussiness"] }
];

const TECGYM_ADMIN_EMAIL_DOMAIN = "@tecgymadmin.pk";

const copyIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const boyAvatarIcon = (
  <svg width="52" height="52" viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <circle cx="32" cy="32" r="32" fill="#1f2937" />
    <circle cx="32" cy="25" r="12" fill="#93c5fd" />
    <path d="M13 57c3.5-11 10-17 19-17s15.5 6 19 17" fill="#60a5fa" />
    <path d="M20 23c2-8 7-12 13-12 7 0 12 4 13 12-7-4-15-4-26 0Z" fill="#111827" />
  </svg>
);


const updateLicensePrivilegeTabs = [
  { label: "License", value: "License" },
  { label: "License Update Request", value: "License Update Request" },
  { label: "Request Status", value: "Request Status" }
];
const rolePrivilegeGroups = [
  {
    label: "Home",
    value: "Home",
    subPrivileges: [
      { label: "Total Clients", value: "Home - Total Clients" },
      { label: "Active Clients", value: "Home - Active Clients" },
      { label: "Inactive Clients", value: "Home - Inactive Clients" }
    ]
  },
  {
    label: "Clients",
    value: "Clients",
    subPrivileges: [
      { label: "All Clients", value: "Clients - All Clients" },
      { label: "Active Clients", value: "Clients - Active Clients" },
      { label: "Near to Expire Clients", value: "Clients - Near to Expire Clients" },
      { label: "Inactive Clients", value: "Clients - Inactive Clients" },
      { label: "Go to Details", value: "Clients - Go to Details" }
    ]
  },
  {
    label: "Go to Details",
    value: "Go to Details",
    subPrivileges: BRANCH_TABS.map((tab) => ({
      label: tab.label,
      value: `Go to Details - ${tab.label}`,
      subPrivileges: tab.id === "UpdateLicense"
        ? updateLicensePrivilegeTabs.map((licenseTab) => ({
            label: licenseTab.label,
            value: `Go to Details - ${tab.label} - ${licenseTab.value}`
          }))
        : []
    }))
  },
  {
    label: "Employee Management",
    value: "Employee Management",
    subPrivileges: [
      { label: "User", value: "Employee Management - User" },
      { label: "Role", value: "Employee Management - Role" },
      { label: "Attendance", value: "Employee Management - Attendance" },
      { label: "Daily Attendance", value: "Employee Management - Daily Attendance" },
      { label: "Apply Leave", value: "Employee Management - Apply Leave" },
      { label: "Apply Work From Home", value: "Employee Management - Apply Work From Home" },
      { label: "Leave Requests", value: "Employee Management - Leave Requests" },
      { label: "Work From Home Requests", value: "Employee Management - Work From Home Requests" }
    ]
  },
  {
    label: "App Activity",
    value: "App Activity",
    subPrivileges: [
      { label: "Activity Users", value: "App Activity - Activity Users" },
      { label: "User Activity Details", value: "App Activity - User Activity Details" }
    ]
  },
  {
    label: "Queries",
    value: "Queries",
    subPrivileges: [
      { label: "Pending", value: "Queries - Pending" },
      { label: "In-Progress", value: "Queries - In-Progress" },
      { label: "Solved", value: "Queries - Solved" },
      { label: "Closed", value: "Queries - Closed" }
    ]
  },
  {
    label: "Tecfolk Activity",
    value: "Tecfolk Activity",
    subPrivileges: []
  },
  {
    label: "Published",
    value: "Published",
    subPrivileges: []
  },
  {
    label: "DataBase Sheets",
    value: "DataBase Sheets",
    subPrivileges: [
      { label: "Vendor", value: "DataBase Sheets - Vendor" },
      { label: "Technician", value: "DataBase Sheets - Technician" },
      { label: "Contact Us", value: "DataBase Sheets - Contact Us" },
      { label: "Login Logs", value: "DataBase Sheets - Login Logs" }
    ]
  },
  {
    label: "Mobile App",
    value: "Mobile App",
    subPrivileges: [
      { label: "Dashboard", value: "Mobile App - Dashboard" },
      { label: "Clients", value: "Mobile App - Clients" },
      { label: "Requested Feature", value: "Mobile App - Requested Feature" }
    ]
  }
];

const sectionStyle = {
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.25)"
};

const userDetailSectionStyle = {
  ...sectionStyle,
  maxWidth: "480px",
  width: "100%"
};

const userDetailHeaderRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  width: "100%"
};

const userDetailHeaderStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  width: "100%"
};

const userDetailTitleStyle = {
  margin: 0,
  fontSize: "24px",
  fontWeight: 800
};

const fieldHintStyle = {
  display: "block",
  marginTop: "6px",
  color: "var(--home-muted, #9ca3af)",
  fontSize: "12px",
  fontWeight: 600
};

const fieldErrorStyle = {
  display: "block",
  marginTop: "6px",
  color: "#fca5a5",
  fontSize: "12px",
  fontWeight: 700
};

const emptyUserFormErrors = {
  name: "",
  number: "",
  role: "",
  gender: "",
  loginMail: "",
  password: "",
  form: ""
};

function UserFormFieldError({ message }) {
  if (!message) {
    return null;
  }

  return <span style={fieldErrorStyle}>{message}</span>;
}

const emailInputGroupStyle = {
  display: "flex",
  alignItems: "stretch",
  marginTop: "8px",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "10px",
  overflow: "hidden",
  backgroundColor: "var(--home-page-bg, #1f232b)"
};

const emailLocalInputStyle = {
  flex: 1,
  border: "none",
  backgroundColor: "transparent",
  color: "var(--home-heading, #fff)",
  padding: "12px 14px",
  fontSize: "14px",
  outline: "none",
  minWidth: 0
};

const emailDomainSuffixStyle = {
  display: "flex",
  alignItems: "center",
  padding: "12px 14px",
  color: "var(--home-muted, #9ca3af)",
  fontSize: "14px",
  fontWeight: 700,
  borderLeft: "1px solid var(--home-border, #444)",
  whiteSpace: "nowrap"
};

function EmployeeManagement() {
  const navigate = useNavigate();
  const loggedInUser = useMemo(() => getAdminUser(), []);
  const isAdminUser = useMemo(() => isLoggedInAdmin(loggedInUser), [loggedInUser]);
  const canAccessEmployeeManagement = canAccessNavRoute("/employee-management");
  const viewerRole = useMemo(
    () => requestViewerRole(getEmployeeName(getCurrentEmployee())),
    []
  );
  const visibleTabs = useMemo(() => {
    const allowedTabs = tabs.filter((tab) => {
      if (!isAdminUser && tab.id === "roles") {
        return false;
      }

      if (viewerRole === "rashid" && (tab.id === "apply-leave" || tab.id === "apply-wfh")) {
        return false;
      }

      return canViewEmployeeManagementTab(tab.id);
    });

    const requestTabs = [];

    if (
      viewerRole === "rashid" ||
      viewerRole === "approver" ||
      canViewEmployeeManagementTab("leave-requests")
    ) {
      requestTabs.push({ id: "leave-requests", label: "Leave Requests" });
    }

    if (
      viewerRole === "rashid" ||
      viewerRole === "approver" ||
      canViewEmployeeManagementTab("wfh-requests")
    ) {
      requestTabs.push({ id: "wfh-requests", label: "Work From Home Requests" });
    }

    return requestTabs.length > 0 ? [...allowedTabs, ...requestTabs] : allowedTabs;
  }, [isAdminUser, viewerRole]);
  const [activeTab, setActiveTab] = useState(() => visibleTabs[0]?.id || "users");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [rolesError, setRolesError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
  const [isUpdateUserOpen, setIsUpdateUserOpen] = useState(false);
  const [isDeleteUserOpen, setIsDeleteUserOpen] = useState(false);
  const [isConfirmDeleteUserOpen, setIsConfirmDeleteUserOpen] = useState(false);
  const [isAddRoleOpen, setIsAddRoleOpen] = useState(false);
  const [isUpdateRoleOpen, setIsUpdateRoleOpen] = useState(false);
  const [isDeleteRoleOpen, setIsDeleteRoleOpen] = useState(false);
  const [isConfirmDeleteRoleOpen, setIsConfirmDeleteRoleOpen] = useState(false);
  const [isPrivilegePopupOpen, setIsPrivilegePopupOpen] = useState(false);
  const [isEditPrivilegePopupOpen, setIsEditPrivilegePopupOpen] = useState(false);
  const [isConfirmSaveRoleOpen, setIsConfirmSaveRoleOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDetailUser, setSelectedDetailUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [userActionLoading, setUserActionLoading] = useState(false);
  const [roleActionLoading, setRoleActionLoading] = useState(false);
  const [isProfileUploadOpen, setIsProfileUploadOpen] = useState(false);
  const [profileUploadError, setProfileUploadError] = useState("");
  const [profileUploadLoading, setProfileUploadLoading] = useState(false);
  const [pendingProfileImage, setPendingProfileImage] = useState("");
  const [profilePhotoRevision, setProfilePhotoRevision] = useState(0);
  const profilePictureInputRef = useRef(null);
  const [newUser, setNewUser] = useState({
    name: "",
    number: "",
    role: "",
    gender: "Male",
    loginMail: "",
    password: ""
  });
  const [newUserErrors, setNewUserErrors] = useState(emptyUserFormErrors);
  const [editUser, setEditUser] = useState({
    name: "",
    number: "",
    role: "",
    gender: "Male",
    loginMail: "",
    password: ""
  });
  const [editUserErrors, setEditUserErrors] = useState(emptyUserFormErrors);
  const [newRole, setNewRole] = useState({
    name: "",
    privileges: []
  });
  const [newRoleError, setNewRoleError] = useState("");
  const [editRole, setEditRole] = useState({
    name: "",
    privileges: []
  });
  const [editRoleError, setEditRoleError] = useState("");
  const [openRolePrivilegesId, setOpenRolePrivilegesId] = useState("");

  useEffect(() => {
    const refreshProfilePhotos = () => {
      setProfilePhotoRevision((current) => current + 1);
    };

    window.addEventListener(ADMIN_PROFILE_PHOTO_UPDATED_EVENT, refreshProfilePhotos);
    return () => window.removeEventListener(ADMIN_PROFILE_PHOTO_UPDATED_EVENT, refreshProfilePhotos);
  }, []);

  useEffect(() => {
    if (!canAccessEmployeeManagement) {
      navigate(getDefaultAccessibleRoute(), { replace: true });
      return;
    }

    if (visibleTabs.length === 0) {
      navigate(getDefaultAccessibleRoute(), { replace: true });
    }
  }, [canAccessEmployeeManagement, navigate, visibleTabs.length]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id || "users");
    }
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    if ((activeTab !== "users" && activeTab !== "attendance" && activeTab !== "daily-attendance") || usersLoaded || usersLoading) {
      return;
    }

    setUsersLoading(true);
    setUsersError("");

    _get(getTecgymAdminUsersURL(), { useCache: false })
      .then((data) => {
        if (!data) {
          throw new Error("Failed to load employees.");
        }

        setUsers(extractList(data));
      })
      .catch((error) => {
        const errorMessage = error.message || "Failed to load employees. Please try again.";
        showToast(errorMessage, "error");
        setUsersError(errorMessage);
      })
      .finally(() => {
        setUsersLoaded(true);
        setUsersLoading(false);
      });
  }, [activeTab, usersLoaded, usersLoading]);

  useEffect(() => {
    if ((activeTab !== "roles" && !isAddUserOpen && !isUpdateUserOpen) || rolesLoaded || rolesLoading) {
      return;
    }

    setRolesLoading(true);
    setRolesError("");

    _get(getTecgymAdminRolesURL(), { useCache: false })
      .then((data) => {
        if (!data) {
          throw new Error("Failed to load roles.");
        }

        if (activeTab === "roles") {
          showApiResponseMessage(data, "success");
        }
        setRoles(extractList(data));
      })
      .catch((error) => {
        showToast(error.message || "Failed to load roles. Please try again.", "error");
        setRolesError(error.message || "Failed to load roles. Please try again.");
      })
      .finally(() => {
        setRolesLoaded(true);
        setRolesLoading(false);
      });
  }, [activeTab, isAddUserOpen, isUpdateUserOpen, rolesLoaded, rolesLoading]);

  useEffect(() => {
    if (isAdminUser || !usersLoaded || usersLoading) {
      return;
    }

    const ownUser = users.find((user) => matchesLoggedInAdminUser(user, loggedInUser));

    if (ownUser) {
      setSelectedDetailUser(ownUser);
    }
  }, [isAdminUser, loggedInUser, users, usersLoaded, usersLoading]);

  const visibleUsers = useMemo(() => {
    if (isAdminUser) {
      return users;
    }

    return users.filter((user) => matchesLoggedInAdminUser(user, loggedInUser));
  }, [isAdminUser, loggedInUser, users]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return visibleUsers.filter((user) => {
      const userRole = getUserRole(user);
      return !query || [
        getUserName(user),
        userRole,
        getUserPhone(user),
        formatUserPhoneDisplay(user),
        getUserLoginMail(user)
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [searchQuery, visibleUsers]);

  const roleSelectOptions = useMemo(() => getRoleSelectOptions(roles), [roles]);
  const sortedRoles = useMemo(() => sortRolesByDisplayOrder(roles), [roles]);
  const sortedFilteredUsers = useMemo(
    () => sortUsersByRoleDisplayOrder(filteredUsers),
    [filteredUsers]
  );

  const handleCopyCredential = async (value, label) => {
    const result = await copyToClipboard(value, { label });
    showToast(result.message, result.success ? "success" : "error");
  };

  const handleViewUserDetails = (user) => {
    setSelectedDetailUser(user);
  };

  const handleOpenProfileUpload = () => {
    if (!selectedDetailUser) {
      return;
    }

    setProfileUploadError("");
    setPendingProfileImage(getUserProfileImage(selectedDetailUser));
    setProfileUploadLoading(false);
    setIsProfileUploadOpen(true);
  };

  const handleCloseProfileUpload = () => {
    setIsProfileUploadOpen(false);
    setProfileUploadError("");
    setPendingProfileImage("");
    setProfileUploadLoading(false);
  };

  const handleProfileUploadClick = () => {
    setProfileUploadError("");
    profilePictureInputRef.current?.click();
  };

  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0];
    setProfileUploadError("");

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setProfileUploadError("Please upload an image file.");
      return;
    }

    if (file.size > 1024 * 1024) {
      setProfileUploadError("Please upload an image under 1MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPendingProfileImage(typeof reader.result === "string" ? reader.result : "");
    };
    reader.onerror = () => {
      setProfileUploadError("Unable to read this image. Please try another file.");
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleSaveProfileImage = async () => {
    if (!selectedDetailUser || !pendingProfileImage) {
      setProfileUploadError("Please choose an image to upload.");
      return;
    }

    const userId = getUserId(selectedDetailUser);

    if (!userId) {
      setProfileUploadError("Unable to update this user because the user id is missing.");
      return;
    }

    const basePayload = buildUserUpdatePayloadFromRecord(selectedDetailUser, {
      profileImage: pendingProfileImage
    });
    const payload = withAppActivityFields(basePayload, {
      activityType: "Update User",
      initiatedFrom: "Users",
      name: getUserName(selectedDetailUser),
      previousValue: getUserProfileImage(selectedDetailUser) || "",
      updatedValue: { profileImage: "[updated]" }
    });

    try {
      setProfileUploadLoading(true);
      const response = await _put(
        updateTecgymAdminUserURL(userId),
        await withEmbeddedActivityLists(userId, payload)
      );

      if (response?.success === false) {
        const message = getApiResponseMessage(response) || "Unable to upload profile image.";
        throw new Error(message);
      }

      showToast("Profile image updated successfully", "success");
      const loginMail = getUserLoginMail(selectedDetailUser);
      if (loginMail && loginMail !== "N/A") {
        saveAdminProfilePicture(loginMail, pendingProfileImage);
      }
      const updatedUser = {
        ...(getUserFromResponse(response) || selectedDetailUser),
        profileImage: pendingProfileImage
      };

      setUsers((currentUsers) => currentUsers.map((user) => (
        getUserId(user) === userId || user === selectedDetailUser ? updatedUser : user
      )));
      setSelectedDetailUser((currentUser) => (
        getUserId(currentUser) === userId || currentUser === selectedDetailUser ? updatedUser : currentUser
      ));
      handleCloseProfileUpload();
    } catch (error) {
      const errorMessage = error.message || "Unable to upload profile image. Please try again.";
      showToast(errorMessage, "error");
      setProfileUploadError(errorMessage);
    } finally {
      setProfileUploadLoading(false);
    }
  };

  const handleBackToUsers = () => {
    handleCloseProfileUpload();
    setSelectedDetailUser(null);
  };

  useEffect(() => {
    if (!isAddUserOpen || roleSelectOptions.length === 0) {
      return;
    }

    setNewUser((currentUser) => {
      if (roleSelectOptions.includes(currentUser.role)) {
        return currentUser;
      }

      return {
        ...currentUser,
        role: roleSelectOptions[0]
      };
    });
  }, [isAddUserOpen, roleSelectOptions]);

  const handleNewUserChange = (field, value) => {
    let nextValue = value;

    if (field === "name") {
      nextValue = sanitizeNameInput(value);
    } else if (field === "number") {
      nextValue = sanitizePhoneInput(value);
    } else if (field === "loginMail") {
      nextValue = sanitizeLoginMailLocalPart(value);
    }

    setNewUser((currentUser) => ({
      ...currentUser,
      [field]: nextValue
    }));
    setNewUserErrors((currentErrors) => ({
      ...currentErrors,
      [field]: "",
      form: ""
    }));
  };

  const handleAddUserClick = () => {
    setNewUser({
      name: "",
      number: "",
      role: roleSelectOptions[0] || "",
      gender: "Male",
      loginMail: "",
      password: ""
    });
    setNewUserErrors(emptyUserFormErrors);
    setUserActionLoading(false);
    setIsAddUserOpen(true);
  };

  const handleCloseAddUser = () => {
    setIsAddUserOpen(false);
    setIsConfirmSaveOpen(false);
    setNewUserErrors(emptyUserFormErrors);
  };

  const handleSaveUserClick = (event) => {
    event.preventDefault();

    const formErrors = getUserFormErrors(newUser, { requirePassword: true });

    if (rolesLoading && roleSelectOptions.length === 0) {
      formErrors.role = formErrors.role || "Please wait for roles to load.";
    } else if (rolesLoaded && roleSelectOptions.length === 0) {
      formErrors.role = "No roles found. Please add a role before creating a user.";
    }

    if (hasUserFormErrors(formErrors)) {
      setNewUserErrors(formErrors);
      return;
    }

    setNewUserErrors(emptyUserFormErrors);
    setIsConfirmSaveOpen(true);
  };

  const handleConfirmSaveUser = async () => {
    const basePayload = {
      ...buildUserPayload(newUser, { includePassword: true }),
      Attendance: [],
      Leaves: [],
      WorkFromHome: []
    };
    const payload = withAppActivityFields(basePayload, {
      activityType: "Add User",
      initiatedFrom: "Users",
      name: basePayload.name,
      updatedValue: buildUserPayload(newUser, { includePassword: false })
    });

    try {
      setUserActionLoading(true);
      const response = await _post(createTecgymAdminUserURL(), payload);

      if (response?.success === false) {
        const message = getApiResponseMessage(response) || "Unable to create user.";
        throw new Error(message);
      }

      showToast("User added successfully", "success");
      const createdUser = getUserFromResponse(response) || {
        id: `local-user-${Date.now()}`,
        ...payload,
        status: "Active"
      };

      setUsers((currentUsers) => [createdUser, ...currentUsers]);
      setUsersLoaded(true);
      setSearchQuery("");
      handleCloseAddUser();
    } catch (error) {
      const errorMessage = error.message || "Unable to create user. Please try again.";
      showToast(errorMessage, "error");
      setIsConfirmSaveOpen(false);
      setNewUserErrors({
        ...emptyUserFormErrors,
        form: errorMessage
      });
    } finally {
      setUserActionLoading(false);
    }
  };

  const handleEditUserChange = (field, value) => {
    if (field === "role" && !isAdminUser) {
      return;
    }

    let nextValue = value;

    if (field === "name") {
      nextValue = sanitizeNameInput(value);
    } else if (field === "number") {
      nextValue = sanitizePhoneInput(value);
    } else if (field === "loginMail") {
      nextValue = sanitizeLoginMailLocalPart(value);
    }

    setEditUser((currentUser) => ({
      ...currentUser,
      [field]: nextValue
    }));
    setEditUserErrors((currentErrors) => ({
      ...currentErrors,
      [field]: "",
      form: ""
    }));
  };

  const handleUpdateUserClick = (user) => {
    const userRole = getUserRole(user);
    const userGender = getUserGender(user);
    const userPhone = getUserPhone(user);
    const userLoginMail = getUserLoginMail(user);

    setSelectedUser(user);
    setEditUser({
      name: getUserName(user) === "N/A" ? "" : String(getUserName(user)),
      number: userPhone === "N/A" ? "" : normalizePakistanPhoneNumber(userPhone),
      role: userRole === "N/A" ? roleSelectOptions[0] || "" : userRole,
      gender: userGender === "N/A" ? "Male" : userGender,
      loginMail: extractLoginMailLocalPart(userLoginMail),
      password: ""
    });
    setEditUserErrors(emptyUserFormErrors);
    setUserActionLoading(false);
    setIsUpdateUserOpen(true);
  };

  const handleCloseUpdateUser = () => {
    setIsUpdateUserOpen(false);
    setSelectedUser(null);
    setEditUserErrors(emptyUserFormErrors);
  };

  const handleSaveUpdatedUser = async (event) => {
    event.preventDefault();

    const formErrors = getUserFormErrors(editUser, {
      requirePassword: false,
      validatePasswordIfProvided: true
    });

    if (isAdminUser) {
      const editRoleOptions = getEditRoleSelectOptions(editUser.role, roles);

      if (rolesLoading && editRoleOptions.length === 0) {
        formErrors.role = formErrors.role || "Please wait for roles to load.";
      } else if (rolesLoaded && editRoleOptions.length === 0) {
        formErrors.role = "No roles found. Please add a role before updating a user.";
      }
    }

    if (hasUserFormErrors(formErrors)) {
      setEditUserErrors(formErrors);
      return;
    }

    const userId = getUserId(selectedUser);

    if (!userId) {
      setEditUserErrors({
        ...emptyUserFormErrors,
        form: "Unable to update this user because the user id is missing."
      });
      return;
    }

    const basePayload = buildUserPayload(editUser, { includePassword: Boolean(normalizeText(editUser.password)) });
    const profileImage = getUserProfileImage(selectedUser);

    if (profileImage) {
      basePayload.profileImage = profileImage;
    }

    if (!isAdminUser && selectedUser) {
      const originalRole = getUserRole(selectedUser);
      basePayload.role = originalRole === "N/A" ? "" : originalRole;
    }

    const { previousValue, updatedValue } = buildUserActivityPayloadValues(selectedUser, basePayload);
    const payload = withAppActivityFields(basePayload, {
      activityType: "Update User",
      initiatedFrom: "Users",
      name: basePayload.name,
      previousValue,
      updatedValue
    });

    try {
      setUserActionLoading(true);
      const response = await _put(
        updateTecgymAdminUserURL(userId),
        await withEmbeddedActivityLists(userId, payload)
      );

      if (response?.success === false) {
        const message = getApiResponseMessage(response) || "Unable to update user.";
        throw new Error(message);
      }

      showToast("User updated successfully", "success");
      const updatedUser = {
        ...(getUserFromResponse(response) || { ...selectedUser, ...payload }),
        ...(basePayload.profileImage ? { profileImage: basePayload.profileImage } : {})
      };

      setUsers((currentUsers) => currentUsers.map((user) => (
        getUserId(user) === userId || user === selectedUser ? updatedUser : user
      )));
      setSelectedDetailUser((currentUser) => (
        getUserId(currentUser) === userId || currentUser === selectedUser ? updatedUser : currentUser
      ));
      handleCloseUpdateUser();
    } catch (error) {
      const errorMessage = error.message || "Unable to update user. Please try again.";
      showToast(errorMessage, "error");
      setEditUserErrors({
        ...emptyUserFormErrors,
        form: errorMessage
      });
    } finally {
      setUserActionLoading(false);
    }
  };

  const handleDeleteUserClick = (user) => {
    setSelectedUser(user);
    setIsDeleteUserOpen(true);
    setIsConfirmDeleteUserOpen(false);
    setUserActionLoading(false);
  };

  const handleCloseDeleteUser = () => {
    setIsDeleteUserOpen(false);
    setIsConfirmDeleteUserOpen(false);
    setSelectedUser(null);
  };

  const handleConfirmDeleteUser = async () => {
    const userId = getUserId(selectedUser);

    if (!userId) {
      showToast("Unable to delete this user because the user id is missing.", "error");
      setIsConfirmDeleteUserOpen(false);
      return;
    }

    try {
      setUserActionLoading(true);
      const response = await _delete(
        deleteTecgymAdminUserURL(userId),
        buildAppActivityFields({
          activityType: "Delete User",
          initiatedFrom: "Users",
          name: getUserName(selectedUser),
          previousValue: buildUserActivitySnapshot(selectedUser)
        })
      );

      if (response?.success === false) {
        const message = getApiResponseMessage(response) || "Unable to delete user.";
        throw new Error(message);
      }

      showToast("User deleted successfully", "success");
      setUsers((currentUsers) => currentUsers.filter((user) => (
        getUserId(user) !== userId && user !== selectedUser
      )));
      setSelectedDetailUser((currentUser) => (
        getUserId(currentUser) === userId || currentUser === selectedUser ? null : currentUser
      ));
      handleCloseDeleteUser();
    } catch (error) {
      const errorMessage = error.message || "Unable to delete user. Please try again.";
      showToast(errorMessage, "error");
      setUsersError(errorMessage);
      setIsConfirmDeleteUserOpen(false);
    } finally {
      setUserActionLoading(false);
    }
  };

  const handleAddRoleClick = () => {
    setNewRole({
      name: "",
      privileges: []
    });
    setNewRoleError("");
    setRoleActionLoading(false);
    setIsAddRoleOpen(true);
  };

  const handleCloseAddRole = () => {
    setIsAddRoleOpen(false);
    setIsPrivilegePopupOpen(false);
    setIsConfirmSaveRoleOpen(false);
    setNewRoleError("");
  };

  const handleNewRoleNameChange = (value) => {
    setNewRole((currentRole) => ({
      ...currentRole,
      name: value
    }));
    setNewRoleError("");
  };

  const handleTogglePrivilege = (privilege) => {
    setNewRole((currentRole) => {
      const hasPrivilege = currentRole.privileges.includes(privilege);

      return {
        ...currentRole,
        privileges: hasPrivilege
          ? currentRole.privileges.filter((item) => item !== privilege)
          : [...currentRole.privileges, privilege]
      };
    });
    setNewRoleError("");
  };

  const handleSaveRoleClick = (event) => {
    event.preventDefault();

    if (!newRole.name.trim() || newRole.privileges.length === 0) {
      setNewRoleError("Please enter role name and select at least one privilege.");
      return;
    }

    setIsConfirmSaveRoleOpen(true);
  };

  const handleConfirmSaveRole = async () => {
    const basePayload = buildRolePayload(newRole);
    const payload = withAppActivityFields(basePayload, {
      activityType: "Add Role",
      initiatedFrom: "Roles",
      name: basePayload.name,
      updatedValue: basePayload
    });

    try {
      setRoleActionLoading(true);
      const response = await _post(createTecgymAdminRoleURL(), payload);

      if (response?.success === false) {
        const message = getApiResponseMessage(response) || "Unable to create role.";
        throw new Error(message);
      }

      showToast("Role added successfully", "success");
      const role = getRoleFromResponse(response) || {
        id: `local-role-${Date.now()}`,
        ...payload,
        description: buildRoleDescription(payload.privileges)
      };

      setRoles((currentRoles) => [role, ...currentRoles]);
      setRolesLoaded(true);
      handleCloseAddRole();
    } catch (error) {
      const errorMessage = error.message || "Unable to create role. Please try again.";
      showToast(errorMessage, "error");
      setIsConfirmSaveRoleOpen(false);
      setNewRoleError(errorMessage);
    } finally {
      setRoleActionLoading(false);
    }
  };

  const handleUpdateRoleClick = (role) => {
    setSelectedRole(role);
    setEditRole({
      name: getRoleName(role) === "N/A" ? "" : getRoleName(role),
      privileges: getRolePrivileges(role)
    });
    setEditRoleError("");
    setRoleActionLoading(false);
    setIsUpdateRoleOpen(true);
  };

  const handleCloseUpdateRole = () => {
    setIsUpdateRoleOpen(false);
    setIsEditPrivilegePopupOpen(false);
    setSelectedRole(null);
    setEditRoleError("");
  };

  const handleEditRoleNameChange = (value) => {
    setEditRole((currentRole) => ({
      ...currentRole,
      name: value
    }));
    setEditRoleError("");
  };

  const handleToggleEditPrivilege = (privilege) => {
    setEditRole((currentRole) => {
      const hasPrivilege = currentRole.privileges.includes(privilege);

      return {
        ...currentRole,
        privileges: hasPrivilege
          ? currentRole.privileges.filter((item) => item !== privilege)
          : [...currentRole.privileges, privilege]
      };
    });
    setEditRoleError("");
  };

  const handleSaveUpdatedRole = async (event) => {
    event.preventDefault();

    if (!editRole.name.trim() || editRole.privileges.length === 0) {
      setEditRoleError("Please enter role name and select at least one privilege.");
      return;
    }

    const roleId = getRoleId(selectedRole);

    if (!roleId) {
      setEditRoleError("Unable to update this role because the role id is missing.");
      return;
    }

    const basePayload = buildRolePayload(editRole);
    const { previousValue, updatedValue } = buildActivityChangeValues(
      buildRoleActivitySnapshot(selectedRole),
      basePayload,
      ["name", "privileges"]
    );
    const payload = withAppActivityFields(basePayload, {
      activityType: "Update Role",
      initiatedFrom: "Roles",
      name: basePayload.name,
      previousValue,
      updatedValue
    });

    try {
      setRoleActionLoading(true);
      const response = await _put(updateTecgymAdminRoleURL(roleId), payload);

      if (response?.success === false) {
        const message = getApiResponseMessage(response) || "Unable to update role.";
        throw new Error(message);
      }

      showToast("Role updated successfully", "success");
      const updatedRole = getRoleFromResponse(response) || {
        ...selectedRole,
        ...payload,
        description: buildRoleDescription(payload.privileges)
      };

      setRoles((currentRoles) => currentRoles.map((role) => (
        getRoleId(role) === roleId || role === selectedRole ? updatedRole : role
      )));
      handleCloseUpdateRole();
    } catch (error) {
      const errorMessage = error.message || "Unable to update role. Please try again.";
      showToast(errorMessage, "error");
      setEditRoleError(errorMessage);
    } finally {
      setRoleActionLoading(false);
    }
  };

  const handleDeleteRoleClick = (role) => {
    setSelectedRole(role);
    setIsDeleteRoleOpen(true);
    setIsConfirmDeleteRoleOpen(false);
    setRoleActionLoading(false);
  };

  const handleCloseDeleteRole = () => {
    setIsDeleteRoleOpen(false);
    setIsConfirmDeleteRoleOpen(false);
    setSelectedRole(null);
  };

  const handleConfirmDeleteRole = async () => {
    const roleId = getRoleId(selectedRole);

    if (!roleId) {
      return;
    }

    try {
      setRoleActionLoading(true);
      const response = await _delete(
        deleteTecgymAdminRoleURL(roleId),
        buildAppActivityFields({
          activityType: "Delete Role",
          initiatedFrom: "Roles",
          name: getRoleName(selectedRole),
          previousValue: buildRoleActivitySnapshot(selectedRole)
        })
      );

      if (response?.success === false) {
        const message = getApiResponseMessage(response) || "Unable to delete role.";
        throw new Error(message);
      }

      showToast("Role deleted successfully", "success");
      setRoles((currentRoles) => currentRoles.filter((role) => (
        getRoleId(role) !== roleId && role !== selectedRole
      )));
      handleCloseDeleteRole();
    } catch (error) {
      const errorMessage = error.message || "Unable to delete role. Please try again.";
      showToast(errorMessage, "error");
      setRolesError(errorMessage);
      setIsConfirmDeleteRoleOpen(false);
    } finally {
      setRoleActionLoading(false);
    }
  };

  const renderPrivilegeOptions = (selectedPrivileges, onTogglePrivilege) => (
    rolePrivilegeGroups.map((privilegeGroup) => (
      <div key={privilegeGroup.value} style={privilegeGroupStyle}>
        <label style={privilegeGroupHeaderStyle}>
          <input
            type="checkbox"
            checked={selectedPrivileges.includes(privilegeGroup.value)}
            onChange={() => onTogglePrivilege(privilegeGroup.value)}
            style={privilegeCheckboxStyle}
          />
          <span style={privilegeGroupTitleStyle}>{privilegeGroup.label}</span>
        </label>

        {(privilegeGroup.subPrivileges || []).length > 0 && (
          <div style={subPrivilegeListStyle}>
            {(privilegeGroup.subPrivileges || []).map((subPrivilege) => (
              <div key={subPrivilege.value} style={subPrivilegeItemStyle}>
                <label style={subPrivilegeOptionStyle}>
                  <input
                    type="checkbox"
                    checked={selectedPrivileges.includes(subPrivilege.value)}
                    onChange={() => onTogglePrivilege(subPrivilege.value)}
                    style={privilegeCheckboxStyle}
                  />
                  <span>{subPrivilege.label}</span>
                </label>

                {subPrivilege.subPrivileges?.length > 0 && (
                  <div style={nestedSubPrivilegeListStyle}>
                    {subPrivilege.subPrivileges.map((nestedSubPrivilege) => (
                      <label key={nestedSubPrivilege.value} style={nestedSubPrivilegeOptionStyle}>
                        <input
                          type="checkbox"
                          checked={selectedPrivileges.includes(nestedSubPrivilege.value)}
                          onChange={() => onTogglePrivilege(nestedSubPrivilege.value)}
                          style={privilegeCheckboxStyle}
                        />
                        <span>{nestedSubPrivilege.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ))
  );

  if (!canAccessEmployeeManagement) {
    return null;
  }

  return (
    <div className="main">
      <Header />
      <div style={pageStyle}>
        <div style={headerCardStyle}>
          <p style={eyebrowStyle}>Employee Management</p>
          <h1 style={titleStyle}>
            {pageHeadings[activeTab] || (isAdminUser ? "Manage Employees" : "My Profile")}
          </h1>
          <p style={subtitleStyle}>
            {pageSubtitles[activeTab] || (isAdminUser
              ? "Choose User to view all employees or Role to view available roles."
              : "View and update your employee profile.")}
          </p>
        </div>

        <div style={tabWrapperStyle}>
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id !== "users") {
                  handleCloseProfileUpload();
                  setSelectedDetailUser(null);
                }
              }}
              style={activeTab === tab.id ? activeTabButtonStyle : tabButtonStyle}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {!activeTab && (
          <div style={emptyStateStyle}>
            Select User to show all employees.
          </div>
        )}

        {activeTab === "users" && (
          <section style={selectedDetailUser ? userDetailSectionStyle : sectionStyle}>
            <div style={sectionHeaderStyle}>
              {selectedDetailUser ? (
                <div style={userDetailHeaderStyle}>
                  <div style={userDetailHeaderRowStyle}>
                    <h2 style={userDetailTitleStyle}>{isAdminUser ? "User Details" : "My Profile"}</h2>
                    {isAdminUser && (
                      <button type="button" style={detailBackButtonStyle} onClick={handleBackToUsers}>
                        Back to Users
                      </button>
                    )}
                  </div>
                  <p style={sectionSubtitleStyle}>
                    {isAdminUser
                      ? "View employee profile, credentials, and actions."
                      : "View your profile, credentials, and update your details."}
                  </p>
                </div>
              ) : isAdminUser ? (
                <>
                  <div>
                    <h2 style={sectionTitleStyle}>All Employees</h2>
                    <p style={sectionSubtitleStyle}>
                      {usersLoading
                        ? "Loading employees..."
                        : `${filteredUsers.length} employee${filteredUsers.length === 1 ? "" : "s"} found`}
                    </p>
                  </div>
                  <div style={userActionsStyle}>
                    <button type="button" style={addUserButtonStyle} onClick={handleAddUserClick}>
                      Add Users
                    </button>
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search employees..."
                      style={searchInputStyle}
                    />
                  </div>
                </>
              ) : (
                <div>
                  <h2 style={sectionTitleStyle}>My Profile</h2>
                  <p style={sectionSubtitleStyle}>
                    {usersLoading ? "Loading your profile..." : "Your employee profile"}
                  </p>
                </div>
              )}
            </div>

            {!selectedDetailUser && usersError && <div style={errorStyle}>{usersError}</div>}
            {!selectedDetailUser && !usersError && usersLoading && (
              <div style={emptyStateStyle}>{isAdminUser ? "Loading employees..." : "Loading your profile..."}</div>
            )}
            {!selectedDetailUser && !usersError && !usersLoading && isAdminUser && filteredUsers.length === 0 && (
              <div style={emptyStateStyle}>No employees found.</div>
            )}
            {!selectedDetailUser && !usersError && !usersLoading && !isAdminUser && (
              <div style={emptyStateStyle}>Unable to load your profile.</div>
            )}
            {selectedDetailUser && !usersLoading && (
              <div style={userDetailScreenStyle}>
                <div style={userDetailProfileStyle}>
                  <button
                    type="button"
                    style={profileAvatarButtonStyle}
                    onClick={handleOpenProfileUpload}
                    title="Upload profile picture"
                    aria-label="Upload profile picture"
                  >
                    <div style={userDetailAvatarStyle}>
                      {getUserProfileImage(selectedDetailUser, profilePhotoRevision) ? (
                        <img src={getUserProfileImage(selectedDetailUser, profilePhotoRevision)} alt={getUserName(selectedDetailUser)} style={userAvatarImageStyle} />
                      ) : (
                        boyAvatarIcon
                      )}
                    </div>
                  </button>
                  <h3 style={userDetailNameStyle}>{getUserName(selectedDetailUser)}</h3>
                </div>

                <div style={userDetailsStyle}>
                  <div style={userDetailRowStyle}>
                    <span style={userDetailLabelStyle}>Number</span>
                    <span>{formatUserPhoneDisplay(selectedDetailUser)}</span>
                  </div>
                  <div style={userDetailRowStyle}>
                    <span style={userDetailLabelStyle}>Role</span>
                    <span style={userRoleBadgeStyle}>{getUserRole(selectedDetailUser)}</span>
                  </div>
                  {getUserGender(selectedDetailUser) !== "N/A" && (
                    <div style={userDetailRowStyle}>
                      <span style={userDetailLabelStyle}>Gender</span>
                      <span>{getUserGender(selectedDetailUser)}</span>
                    </div>
                  )}
                  {getUserLoginMail(selectedDetailUser) !== "N/A" && (
                    <div style={userDetailRowStyle}>
                      <span style={userDetailLabelStyle}>Login ID</span>
                      <span style={userCredentialGroupStyle}>
                        <span style={userCredentialValueStyle}>{getUserLoginMail(selectedDetailUser)}</span>
                        <button
                          type="button"
                          style={userCredentialCopyButtonStyle}
                          title="Copy login ID"
                          aria-label="Copy login ID"
                          onClick={() => handleCopyCredential(getUserLoginMail(selectedDetailUser), "Employee login ID")}
                        >
                          {copyIcon}
                        </button>
                      </span>
                    </div>
                  )}
                  {getUserLoginPassword(selectedDetailUser) !== "N/A" && (
                    <div style={userDetailRowStyle}>
                      <span style={userDetailLabelStyle}>Login Password</span>
                      <span style={userCredentialGroupStyle}>
                        <span style={userCredentialValueStyle}>{getUserLoginPassword(selectedDetailUser)}</span>
                        <button
                          type="button"
                          style={userCredentialCopyButtonStyle}
                          title="Copy login password"
                          aria-label="Copy login password"
                          onClick={() => handleCopyCredential(getUserLoginPassword(selectedDetailUser), "Employee login password")}
                        >
                          {copyIcon}
                        </button>
                      </span>
                    </div>
                  )}
                </div>

                <div style={userCardActionsStyle}>
                  <button type="button" style={cardUpdateButtonStyle} onClick={() => handleUpdateUserClick(selectedDetailUser)}>
                    Update
                  </button>
                  {isAdminUser && (
                    <button type="button" style={cardDeleteButtonStyle} onClick={() => handleDeleteUserClick(selectedDetailUser)}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            )}
            {!selectedDetailUser && !usersError && !usersLoading && isAdminUser && filteredUsers.length > 0 && (
              <div style={userGridStyle}>
                {sortedFilteredUsers.map((user, index) => (
                  <article key={user._id || user.id || user.email || getUserPhone(user) || index} style={userCardStyle}>
                    <div style={compactUserCardHeaderStyle}>
                      <div style={userAvatarStyle}>
                        {getUserProfileImage(user, profilePhotoRevision) ? (
                          <img src={getUserProfileImage(user, profilePhotoRevision)} alt={getUserName(user)} style={userAvatarImageStyle} />
                        ) : (
                          boyAvatarIcon
                        )}
                      </div>
                      <div style={compactUserSummaryStyle}>
                        <span style={compactUserNameStyle} title={getUserName(user)}>{getUserName(user)}</span>
                        <span style={userRoleBadgeStyle} title={getUserRole(user)}>{getUserRole(user)}</span>
                      </div>
                    </div>
                    <div style={userCardActionsStyle}>
                      <button type="button" style={viewDetailsButtonStyle} onClick={() => handleViewUserDetails(user)}>
                        View User Details
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {isAddUserOpen && (
          <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-label="Add user popup">
            <div style={modalCardStyle}>
              <div style={modalHeaderStyle}>
                <div>
                  <h2 style={modalTitleStyle}>Add User</h2>
                  <p style={modalSubtitleStyle}>Enter employee details below.</p>
                </div>
                <button type="button" style={closeButtonStyle} onClick={handleCloseAddUser} aria-label="Close add user popup">
                  X
                </button>
              </div>

              <form onSubmit={handleSaveUserClick}>
                <label style={fieldLabelStyle}>
                  Employee Name
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(event) => handleNewUserChange("name", event.target.value)}
                    placeholder="Enter employee name"
                    style={formInputStyle}
                  />
                  <UserFormFieldError message={newUserErrors.name} />
                </label>

                <label style={fieldLabelStyle}>
                  Number
                  <input
                    type="tel"
                    value={newUser.number}
                    onChange={(event) => handleNewUserChange("number", event.target.value)}
                    placeholder="3001234567"
                    inputMode="numeric"
                    maxLength={11}
                    style={formInputStyle}
                  />
                  {newUserErrors.number ? (
                    <UserFormFieldError message={newUserErrors.number} />
                  ) : (
                    <span style={fieldHintStyle}>10-digit Pakistan mobile number (without +92)</span>
                  )}
                </label>

                <label style={fieldLabelStyle}>
                  Role
                  <select
                    value={newUser.role}
                    onChange={(event) => handleNewUserChange("role", event.target.value)}
                    style={formInputStyle}
                    disabled={rolesLoading && roleSelectOptions.length === 0}
                  >
                    {rolesLoading && roleSelectOptions.length === 0 && (
                      <option value="">Loading roles...</option>
                    )}
                    {!rolesLoading && roleSelectOptions.length === 0 && (
                      <option value="">No roles found</option>
                    )}
                    {roleSelectOptions.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                  <UserFormFieldError message={newUserErrors.role} />
                </label>

                <div style={fieldLabelStyle}>
                  Gender
                  <div style={genderOptionsStyle}>
                    {["Male", "Female"].map((gender) => (
                      <label key={gender} style={genderOptionStyle}>
                        <input
                          type="radio"
                          name="new-user-gender"
                          value={gender}
                          checked={newUser.gender === gender}
                          onChange={(event) => handleNewUserChange("gender", event.target.value)}
                          style={genderRadioStyle}
                        />
                        <span>{gender}</span>
                      </label>
                    ))}
                  </div>
                  <UserFormFieldError message={newUserErrors.gender} />
                </div>

                <label style={fieldLabelStyle}>
                  Login Mail
                  <div style={emailInputGroupStyle}>
                    <input
                      type="text"
                      value={newUser.loginMail}
                      onChange={(event) => handleNewUserChange("loginMail", event.target.value)}
                      placeholder="username"
                      autoComplete="off"
                      style={emailLocalInputStyle}
                    />
                    <span style={emailDomainSuffixStyle}>{TECGYM_ADMIN_EMAIL_DOMAIN}</span>
                  </div>
                  <UserFormFieldError message={newUserErrors.loginMail} />
                </label>

                <label style={fieldLabelStyle}>
                  Password
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(event) => handleNewUserChange("password", event.target.value)}
                    placeholder="Enter password"
                    autoComplete="new-password"
                    style={formInputStyle}
                  />
                  <UserFormFieldError message={newUserErrors.password} />
                </label>

                <UserFormFieldError message={newUserErrors.form} />

                <div style={modalActionsStyle}>
                  <button type="button" style={secondaryButtonStyle} onClick={handleCloseAddUser}>
                    Cancel
                  </button>
                  <button type="submit" style={primaryButtonStyle}>
                    Save User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isConfirmSaveOpen && (
          <div style={confirmOverlayStyle} role="dialog" aria-modal="true" aria-label="Confirm save user">
            <div style={confirmCardStyle}>
              <h3 style={confirmTitleStyle}>Save User?</h3>
              <p style={confirmTextStyle}>Are you sure you want to save this user?</p>
              <div style={confirmActionsStyle}>
                <button type="button" style={secondaryButtonStyle} onClick={() => setIsConfirmSaveOpen(false)}>
                  No
                </button>
                <button type="button" style={primaryButtonStyle} onClick={handleConfirmSaveUser} disabled={userActionLoading}>
                  {userActionLoading ? "Saving..." : "Yes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isUpdateUserOpen && (
          <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-label="Update user popup">
            <div style={modalCardStyle}>
              <div style={modalHeaderStyle}>
                <div>
                  <h2 style={modalTitleStyle}>Update User</h2>
                  <p style={modalSubtitleStyle}>Update employee details below.</p>
                </div>
                <button type="button" style={closeButtonStyle} onClick={handleCloseUpdateUser} aria-label="Close update user popup">
                  X
                </button>
              </div>

              <form onSubmit={handleSaveUpdatedUser}>
                <label style={fieldLabelStyle}>
                  Employee Name
                  <input
                    type="text"
                    value={editUser.name}
                    onChange={(event) => handleEditUserChange("name", event.target.value)}
                    placeholder="Enter employee name"
                    style={formInputStyle}
                  />
                  <UserFormFieldError message={editUserErrors.name} />
                </label>

                <label style={fieldLabelStyle}>
                  Number
                  <input
                    type="tel"
                    value={editUser.number}
                    onChange={(event) => handleEditUserChange("number", event.target.value)}
                    placeholder="3001234567"
                    inputMode="numeric"
                    maxLength={11}
                    style={formInputStyle}
                  />
                  {editUserErrors.number ? (
                    <UserFormFieldError message={editUserErrors.number} />
                  ) : (
                    <span style={fieldHintStyle}>10-digit Pakistan mobile number (without +92)</span>
                  )}
                </label>

                <label style={fieldLabelStyle}>
                  Role
                  {isAdminUser ? (
                    <select
                      value={editUser.role}
                      onChange={(event) => handleEditUserChange("role", event.target.value)}
                      style={formInputStyle}
                      disabled={rolesLoading && getEditRoleSelectOptions(editUser.role, roles).length === 0}
                    >
                      {rolesLoading && getEditRoleSelectOptions(editUser.role, roles).length === 0 && (
                        <option value="">Loading roles...</option>
                      )}
                      {!rolesLoading && getEditRoleSelectOptions(editUser.role, roles).length === 0 && (
                        <option value="">No roles found</option>
                      )}
                      {getEditRoleSelectOptions(editUser.role, roles).map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={editUser.role}
                      readOnly
                      style={{ ...formInputStyle, opacity: 0.7, cursor: "not-allowed" }}
                    />
                  )}
                  <UserFormFieldError message={editUserErrors.role} />
                </label>

                <div style={fieldLabelStyle}>
                  Gender
                  <div style={genderOptionsStyle}>
                    {["Male", "Female"].map((gender) => (
                      <label key={gender} style={genderOptionStyle}>
                        <input
                          type="radio"
                          name="edit-user-gender"
                          value={gender}
                          checked={editUser.gender === gender}
                          onChange={(event) => handleEditUserChange("gender", event.target.value)}
                          style={genderRadioStyle}
                        />
                        <span>{gender}</span>
                      </label>
                    ))}
                  </div>
                  <UserFormFieldError message={editUserErrors.gender} />
                </div>

                <label style={fieldLabelStyle}>
                  Login Mail
                  <div style={emailInputGroupStyle}>
                    <input
                      type="text"
                      value={editUser.loginMail}
                      onChange={(event) => handleEditUserChange("loginMail", event.target.value)}
                      placeholder="username"
                      autoComplete="off"
                      style={emailLocalInputStyle}
                    />
                    <span style={emailDomainSuffixStyle}>{TECGYM_ADMIN_EMAIL_DOMAIN}</span>
                  </div>
                  <UserFormFieldError message={editUserErrors.loginMail} />
                </label>

                <label style={fieldLabelStyle}>
                  Password
                  <input
                    type="password"
                    value={editUser.password}
                    onChange={(event) => handleEditUserChange("password", event.target.value)}
                    placeholder="Leave blank to keep current password"
                    autoComplete="new-password"
                    style={formInputStyle}
                  />
                  <UserFormFieldError message={editUserErrors.password} />
                </label>

                <UserFormFieldError message={editUserErrors.form} />

                <div style={modalActionsStyle}>
                  <button type="button" style={secondaryButtonStyle} onClick={handleCloseUpdateUser}>
                    Cancel
                  </button>
                  <button type="submit" style={primaryButtonStyle} disabled={userActionLoading}>
                    {userActionLoading ? "Updating..." : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isDeleteUserOpen && selectedUser && (
          <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-label="Delete user popup">
            <div style={confirmCardStyle}>
              <h3 style={confirmTitleStyle}>Delete User</h3>
              <div style={deleteUserDetailsStyle}>
                <div style={userDetailRowStyle}>
                  <span style={userDetailLabelStyle}>Name</span>
                  <span style={userNameValueStyle}>{getUserName(selectedUser)}</span>
                </div>
                <div style={userDetailRowStyle}>
                  <span style={userDetailLabelStyle}>Roles</span>
                  <span style={userRoleBadgeStyle}>{getUserRole(selectedUser)}</span>
                </div>
              </div>
              <div style={confirmActionsStyle}>
                <button type="button" style={secondaryButtonStyle} onClick={handleCloseDeleteUser}>
                  Cancel
                </button>
                <button type="button" style={dangerButtonStyle} onClick={() => setIsConfirmDeleteUserOpen(true)} disabled={!getUserId(selectedUser)}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {isConfirmDeleteUserOpen && selectedUser && (
          <div style={confirmOverlayStyle} role="dialog" aria-modal="true" aria-label="Confirm delete user">
            <div style={confirmCardStyle}>
              <h3 style={confirmTitleStyle}>Delete User?</h3>
              <p style={confirmTextStyle}>Are you sure you want to delete {getUserName(selectedUser)}?</p>
              <div style={confirmActionsStyle}>
                <button type="button" style={secondaryButtonStyle} onClick={() => setIsConfirmDeleteUserOpen(false)}>
                  No
                </button>
                <button type="button" style={dangerButtonStyle} onClick={handleConfirmDeleteUser} disabled={userActionLoading}>
                  {userActionLoading ? "Deleting..." : "Yes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isProfileUploadOpen && selectedDetailUser && (
          <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-label="Upload profile picture">
            <div style={modalCardStyle}>
              <div style={modalHeaderStyle}>
                <div>
                  <h2 style={modalTitleStyle}>Upload Profile Picture</h2>
                  <p style={modalSubtitleStyle}>Choose an image for {getUserName(selectedDetailUser)}.</p>
                </div>
                <button type="button" style={closeButtonStyle} onClick={handleCloseProfileUpload} aria-label="Close profile upload popup">
                  X
                </button>
              </div>

              <div style={profileUploadPreviewStyle}>
                {pendingProfileImage ? (
                  <img src={pendingProfileImage} alt="Profile preview" style={profileUploadPreviewImageStyle} />
                ) : (
                  <div style={profileUploadPlaceholderStyle}>
                    <div style={profileUploadAvatarIconStyle}>{boyAvatarIcon}</div>
                  </div>
                )}
              </div>

              <input
                ref={profilePictureInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfileImageChange}
                style={hiddenFileInputStyle}
              />

              {profileUploadError && <div style={formErrorStyle}>{profileUploadError}</div>}

              <div style={profileUploadActionsStyle}>
                <button type="button" style={secondaryButtonStyle} onClick={handleCloseProfileUpload}>
                  Cancel
                </button>
                <button type="button" style={secondaryButtonStyle} onClick={handleProfileUploadClick}>
                  Choose Image
                </button>
                <button
                  type="button"
                  style={primaryButtonStyle}
                  onClick={handleSaveProfileImage}
                  disabled={profileUploadLoading || !pendingProfileImage}
                >
                  {profileUploadLoading ? "Uploading..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "attendance" && (
          <AttendanceTab
            users={visibleUsers}
            usersLoading={usersLoading}
            isAdminUser={isAdminUser}
            loggedInUser={loggedInUser}
          />
        )}

        {activeTab === "daily-attendance" && (
          <DailyAttendanceTab
            users={users}
            usersLoading={usersLoading}
            usersError={usersError}
          />
        )}

        {activeTab === "apply-leave" && <ApplyRequestPanel mode="leave" />}
        {activeTab === "apply-wfh" && <ApplyRequestPanel mode="wfh" />}
        {activeTab === "leave-requests" && <ApplyRequestPanel mode="leave" audience="others" />}
        {activeTab === "wfh-requests" && <ApplyRequestPanel mode="wfh" audience="others" />}

        {activeTab === "roles" && (
          <section style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h2 style={sectionTitleStyle}>Roles</h2>
                <p style={sectionSubtitleStyle}>{rolesLoading ? "Loading roles..." : `${roles.length} role${roles.length === 1 ? "" : "s"} found`}</p>
              </div>
              <button type="button" style={addUserButtonStyle} onClick={handleAddRoleClick}>
                Add Role
              </button>
            </div>

            {rolesError && <div style={errorStyle}>{rolesError}</div>}
            {!rolesError && rolesLoading && <div style={emptyStateStyle}>Loading roles...</div>}
            {!rolesError && !rolesLoading && roles.length === 0 && (
              <div style={emptyStateStyle}>No roles found.</div>
            )}
            {!rolesError && !rolesLoading && roles.length > 0 && (
              <div style={roleGridStyle}>
                {sortedRoles.map((role, index) => {
                  const roleDropdownId = getRoleDropdownId(role, index);
                  const rolePrivileges = getRolePrivileges(role);
                  const isPrivilegeDropdownOpen = openRolePrivilegesId === roleDropdownId;

                  return (
                    <div key={roleDropdownId} style={roleCardStyle}>
                      <h3 style={roleTitleStyle}>{getRoleName(role)}</h3>
                      <p style={roleMetaStyle}>{getRoleMeta(role)}</p>

                      <div style={rolePrivilegesDropdownStyle}>
                        <button
                          type="button"
                          style={rolePrivilegesToggleStyle}
                          onClick={() => setOpenRolePrivilegesId(isPrivilegeDropdownOpen ? "" : roleDropdownId)}
                        >
                          <span>Privileges</span>
                          <span>{isPrivilegeDropdownOpen ? "Hide" : "Show"} ({rolePrivileges.length})</span>
                        </button>

                        {isPrivilegeDropdownOpen && (
                          <div style={rolePrivilegesListStyle}>
                            {rolePrivileges.length > 0 ? (
                              rolePrivileges.map((privilege) => (
                                <span key={privilege} style={rolePrivilegeItemStyle}>{privilege}</span>
                              ))
                            ) : (
                              <span style={roleNoPrivilegesStyle}>No privileges selected</span>
                            )}
                          </div>
                        )}
                      </div>

                      <div style={userCardActionsStyle}>
                        <button type="button" style={cardUpdateButtonStyle} onClick={() => handleUpdateRoleClick(role)}>
                          Update
                        </button>
                        <button type="button" style={cardDeleteButtonStyle} onClick={() => handleDeleteRoleClick(role)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {isAddRoleOpen && (
          <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-label="Add role popup">
            <div style={modalCardStyle}>
              <div style={modalHeaderStyle}>
                <div>
                  <h2 style={modalTitleStyle}>Add Role</h2>
                  <p style={modalSubtitleStyle}>Enter role name and choose privileges.</p>
                </div>
                <button type="button" style={closeButtonStyle} onClick={handleCloseAddRole} aria-label="Close add role popup">
                  X
                </button>
              </div>

              <form onSubmit={handleSaveRoleClick}>
                <label style={fieldLabelStyle}>
                  Role Name
                  <input
                    type="text"
                    value={newRole.name}
                    onChange={(event) => handleNewRoleNameChange(event.target.value)}
                    placeholder="Enter role name"
                    style={formInputStyle}
                  />
                </label>

                <div style={privilegeSummaryStyle}>
                  <div>
                    <p style={privilegeSummaryTitleStyle}>Privileges</p>
                    <p style={privilegeSummaryTextStyle}>
                      {newRole.privileges.length === 0
                        ? "No privileges selected"
                        : `${newRole.privileges.length} selected`}
                    </p>
                  </div>
                  <button type="button" style={secondaryButtonStyle} onClick={() => setIsPrivilegePopupOpen(true)}>
                    Select Privileges
                  </button>
                </div>

                {newRoleError && <div style={formErrorStyle}>{newRoleError}</div>}

                <div style={modalActionsStyle}>
                  <button type="button" style={secondaryButtonStyle} onClick={handleCloseAddRole}>
                    Cancel
                  </button>
                  <button type="submit" style={primaryButtonStyle}>
                    Save Role
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isConfirmSaveRoleOpen && (
          <div style={confirmOverlayStyle} role="dialog" aria-modal="true" aria-label="Confirm save role">
            <div style={confirmCardStyle}>
              <h3 style={confirmTitleStyle}>Save Role?</h3>
              <p style={confirmTextStyle}>Are you sure you want to save this role?</p>
              <div style={confirmActionsStyle}>
                <button type="button" style={secondaryButtonStyle} onClick={() => setIsConfirmSaveRoleOpen(false)}>
                  No
                </button>
                <button type="button" style={primaryButtonStyle} onClick={handleConfirmSaveRole} disabled={roleActionLoading}>
                  {roleActionLoading ? "Saving..." : "Yes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isUpdateRoleOpen && selectedRole && (
          <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-label="Update role popup">
            <div style={modalCardStyle}>
              <div style={modalHeaderStyle}>
                <div>
                  <h2 style={modalTitleStyle}>Update Role</h2>
                  <p style={modalSubtitleStyle}>Update role name and privileges.</p>
                </div>
                <button type="button" style={closeButtonStyle} onClick={handleCloseUpdateRole} aria-label="Close update role popup">
                  X
                </button>
              </div>

              <form onSubmit={handleSaveUpdatedRole}>
                <label style={fieldLabelStyle}>
                  Role Name
                  <input
                    type="text"
                    value={editRole.name}
                    onChange={(event) => handleEditRoleNameChange(event.target.value)}
                    placeholder="Enter role name"
                    style={formInputStyle}
                  />
                </label>

                <div style={privilegeSummaryStyle}>
                  <div>
                    <p style={privilegeSummaryTitleStyle}>Privileges</p>
                    <p style={privilegeSummaryTextStyle}>
                      {editRole.privileges.length === 0
                        ? "No privileges selected"
                        : `${editRole.privileges.length} selected`}
                    </p>
                  </div>
                  <button type="button" style={secondaryButtonStyle} onClick={() => setIsEditPrivilegePopupOpen(true)}>
                    Select Privileges
                  </button>
                </div>

                {editRoleError && <div style={formErrorStyle}>{editRoleError}</div>}

                <div style={modalActionsStyle}>
                  <button type="button" style={secondaryButtonStyle} onClick={handleCloseUpdateRole}>
                    Cancel
                  </button>
                  <button type="submit" style={primaryButtonStyle} disabled={roleActionLoading}>
                    {roleActionLoading ? "Updating..." : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isDeleteRoleOpen && selectedRole && (
          <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-label="Delete role popup">
            <div style={confirmCardStyle}>
              <h3 style={confirmTitleStyle}>Delete Role</h3>
              <div style={deleteUserDetailsStyle}>
                <div style={userDetailRowStyle}>
                  <span style={userDetailLabelStyle}>Role</span>
                  <span style={userNameValueStyle}>{getRoleName(selectedRole)}</span>
                </div>
                <div style={userDetailRowStyle}>
                  <span style={userDetailLabelStyle}>Privileges</span>
                  <span>{getRolePrivileges(selectedRole).length}</span>
                </div>
              </div>
              <div style={confirmActionsStyle}>
                <button type="button" style={secondaryButtonStyle} onClick={handleCloseDeleteRole}>
                  Cancel
                </button>
                <button type="button" style={dangerButtonStyle} onClick={() => setIsConfirmDeleteRoleOpen(true)} disabled={!getRoleId(selectedRole)}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {isConfirmDeleteRoleOpen && selectedRole && (
          <div style={confirmOverlayStyle} role="dialog" aria-modal="true" aria-label="Confirm delete role">
            <div style={confirmCardStyle}>
              <h3 style={confirmTitleStyle}>Delete Role?</h3>
              <p style={confirmTextStyle}>Are you sure you want to delete {getRoleName(selectedRole)}?</p>
              <div style={confirmActionsStyle}>
                <button type="button" style={secondaryButtonStyle} onClick={() => setIsConfirmDeleteRoleOpen(false)}>
                  No
                </button>
                <button type="button" style={dangerButtonStyle} onClick={handleConfirmDeleteRole} disabled={roleActionLoading}>
                  {roleActionLoading ? "Deleting..." : "Yes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isPrivilegePopupOpen && (
          <div style={privilegeOverlayStyle} role="dialog" aria-modal="true" aria-label="Select privileges popup">
            <div style={privilegeCardStyle}>
              <div style={modalHeaderStyle}>
                <div>
                  <h2 style={modalTitleStyle}>Select Privileges</h2>
                  <p style={modalSubtitleStyle}>Choose permissions for this role.</p>
                </div>
                <button type="button" style={closeButtonStyle} onClick={() => setIsPrivilegePopupOpen(false)} aria-label="Close privileges popup">
                  X
                </button>
              </div>

              <div style={privilegeListStyle}>
                {renderPrivilegeOptions(newRole.privileges, handleTogglePrivilege)}
              </div>

              <div style={modalActionsStyle}>
                <button type="button" style={secondaryButtonStyle} onClick={() => setNewRole((currentRole) => ({ ...currentRole, privileges: [] }))}>
                  Clear
                </button>
                <button type="button" style={primaryButtonStyle} onClick={() => setIsPrivilegePopupOpen(false)}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {isEditPrivilegePopupOpen && (
          <div style={privilegeOverlayStyle} role="dialog" aria-modal="true" aria-label="Select role privileges popup">
            <div style={privilegeCardStyle}>
              <div style={modalHeaderStyle}>
                <div>
                  <h2 style={modalTitleStyle}>Select Privileges</h2>
                  <p style={modalSubtitleStyle}>Choose permissions for this role.</p>
                </div>
                <button type="button" style={closeButtonStyle} onClick={() => setIsEditPrivilegePopupOpen(false)} aria-label="Close role privileges popup">
                  X
                </button>
              </div>

              <div style={privilegeListStyle}>
                {renderPrivilegeOptions(editRole.privileges, handleToggleEditPrivilege)}
              </div>

              <div style={modalActionsStyle}>
                <button type="button" style={secondaryButtonStyle} onClick={() => setEditRole((currentRole) => ({ ...currentRole, privileges: [] }))}>
                  Clear
                </button>
                <button type="button" style={primaryButtonStyle} onClick={() => setIsEditPrivilegePopupOpen(false)}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function extractList(data) {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data.map(normalizeAdminUserRecord).filter(Boolean);
  }

  const keys = [
    "response",
    "data",
    "users",
    "tecgymAdminUsers",
    "adminUsers",
    "employees",
    "roles",
    "tecgymAdminRoles",
    "adminRoles",
    "result",
    "results",
    "items"
  ];

  for (const key of keys) {
    const value = data[key];
    if (Array.isArray(value)) {
      return value.map(normalizeAdminUserRecord).filter(Boolean);
    }
    if (value && typeof value === "object") {
      const nestedList = extractList(value);
      if (nestedList.length > 0) return nestedList;
    }
  }

  const normalized = normalizeAdminUserRecord(data);
  if (
    normalized?._id ||
    normalized?.id ||
    normalized?.email ||
    normalized?.loginId ||
    normalized?.name ||
    normalized?.roleName ||
    normalized?.title ||
    normalized?.role ||
    normalized?.role_name
  ) {
    return [normalized];
  }

  return [];
}

function normalizeAdminUserRecord(record) {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return record;
  }

  const nested = record.user || record.tecgymAdminUser || record.adminUser;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested;
  }

  return record;
}

function getUserName(user) {
  return user.name || user.userName || user.fullName || user.employeeName || user.firstName || "N/A";
}

function getUserId(user) {
  return String(
    user?._id ||
    user?.id ||
    user?.userId ||
    user?.userID ||
    user?.adminUserId ||
    user?.adminUserID ||
    user?.tecgymAdminUserId ||
    user?.tecgymAdminUserID ||
    ""
  ).trim();
}

function getUserRole(user) {
  if (user.designation) return user.designation;
  if (typeof user.role === "string") return user.role;
  if (user.role && typeof user.role === "object") {
    return user.role.name || user.role.roleName || user.role.title || "N/A";
  }
  return user.roleName || user.userRole || "N/A";
}

function getUserPhone(user) {
  const phone = user.number || user.phone || user.contactNumber || user.phoneNumber || user.mobile;

  if (phone === undefined || phone === null || phone === "") {
    return "N/A";
  }

  return String(phone);
}

function formatUserPhoneDisplay(user) {
  const phone = getUserPhone(user);

  if (phone === "N/A") {
    return "N/A";
  }

  const trimmed = phone.trim();

  if (trimmed.startsWith("+92")) {
    return trimmed;
  }

  let digits = trimmed.replace(/\D/g, "");

  if (digits.startsWith("92")) {
    return `+${digits}`;
  }

  if (digits.startsWith("0")) {
    digits = digits.slice(1);
  }

  return `+92${digits}`;
}

function getUserGender(user) {
  return user.gender || user.sex || "N/A";
}

function getUserLoginMail(user) {
  return user.loginId || user.loginID || user.userLoginId || user.loginMail || user.email || user.loginEmail || user.mail || "N/A";
}

function getUserLoginPassword(user) {
  return user.password || user.loginPassword || user.userPassword || user.pass || "N/A";
}

function readProfileImageValue(image) {
  if (typeof image === "string") {
    return image.trim();
  }

  if (image && typeof image === "object") {
    return readProfileImageValue(
      image.url || image.path || image.src || image.filename || image.fileName || ""
    );
  }

  return "";
}

function resolveProfileImageUrl(image) {
  const value = readProfileImageValue(image);
  if (!value || value.startsWith("data:") || value.startsWith("blob:") || /^https?:\/\//i.test(value)) {
    return value;
  }

  const server = getLocalServerURL().replace(/\/+$/, "");
  return `${server}/${value.replace(/^\/+/, "")}`;
}

function getUserProfileImage(user) {
  const image = readProfileImageValue(
    user?.profileImage ||
    user?.profilePhoto ||
    user?.photo ||
    user?.avatar ||
    user?.image ||
    user?.imageUrl ||
    user?.profileImageUrl ||
    user?.picture ||
    user?.pictureUrl ||
    user?.profile?.image ||
    user?.profile?.photo
  );

  if (image) {
    return resolveProfileImageUrl(image);
  }

  const loginMail = getUserLoginMail(user);
  if (!loginMail || loginMail === "N/A") {
    return "";
  }

  return resolveProfileImageUrl(getAdminProfilePicture(loginMail) || "");
}

function getEditRoleSelectOptions(selectedRole, roles) {
  return [...new Set([selectedRole, ...getRoleSelectOptions(roles)].filter(Boolean))];
}

function normalizeRoleKey(roleName) {
  return String(roleName || "").trim().toLowerCase();
}

function getRoleDisplaySortIndex(roleName) {
  const roleKey = normalizeRoleKey(roleName);

  for (let index = 0; index < ROLE_SECTION_ORDER.length; index += 1) {
    if (ROLE_SECTION_ORDER[index].aliases.includes(roleKey)) {
      return index;
    }
  }

  return ROLE_SECTION_ORDER.length;
}

function compareByRoleDisplayOrder(roleNameA, roleNameB) {
  const indexDiff = getRoleDisplaySortIndex(roleNameA) - getRoleDisplaySortIndex(roleNameB);

  if (indexDiff !== 0) {
    return indexDiff;
  }

  return String(roleNameA).localeCompare(String(roleNameB));
}

function sortRolesByDisplayOrder(roles) {
  return [...roles].sort((roleA, roleB) => compareByRoleDisplayOrder(getRoleName(roleA), getRoleName(roleB)));
}

function sortUsersByRoleDisplayOrder(users) {
  return [...users].sort((userA, userB) => {
    const roleDiff = compareByRoleDisplayOrder(getUserRole(userA), getUserRole(userB));

    if (roleDiff !== 0) {
      return roleDiff;
    }

    return getUserName(userA).localeCompare(getUserName(userB));
  });
}

function getRoleSelectOptions(roles) {
  return [...new Set(
    sortRolesByDisplayOrder(roles)
      .map(getRoleName)
      .filter((roleName) => roleName !== "N/A")
  )];
}

function getRoleId(role) {
  return String(role?._id || role?.id || role?.roleId || role?.roleID || "").trim();
}

function showApiResponseMessage(response, type = "info") {
  const message = getApiResponseMessage(response);

  if (message) {
    showToast(message, response?.success === false ? "error" : type);
  }
}

function getApiResponseMessage(response) {
  if (typeof response === "string") {
    return response.trim();
  }

  const message = response?.message ||
    response?.data?.message ||
    response?.response?.message ||
    response?.error ||
    response?.data?.error ||
    response?.response?.error;

  return typeof message === "string" ? message.trim() : "";
}

async function withEmbeddedActivityLists(userId, payload) {
  try {
    const users = await fetchTecgymAdminUsers();
    const latestUser = users.find((user) => getUserId(user) === userId);
    if (!latestUser) {
      return payload;
    }

    return {
      ...payload,
      ...buildUserActivityLists(latestUser)
    };
  } catch {
    return payload;
  }
}

function buildUserUpdatePayloadFromRecord(user, overrides = {}) {
  const number = getUserPhone(user);
  const loginMail = getUserLoginMail(user);
  const gender = getUserGender(user);
  const payload = {
    name: getUserName(user) === "N/A" ? "" : getUserName(user),
    number: number === "N/A" ? "" : number,
    phone: number === "N/A" ? "" : number,
    role: getUserRole(user) === "N/A" ? "" : getUserRole(user),
    ...overrides
  };

  if (gender !== "N/A") {
    payload.gender = gender;
  }

  if (loginMail !== "N/A") {
    payload.loginId = loginMail;
    payload.loginMail = loginMail;
    payload.email = loginMail;
  }

  return payload;
}

function normalizeText(value) {
  return String(value ?? "").trim();
}

function sanitizeNameInput(value) {
  return String(value).replace(/\d/g, "");
}

function sanitizePhoneInput(value) {
  const digits = String(value).replace(/\D/g, "");

  if (digits.startsWith("0")) {
    return digits.slice(0, 11);
  }

  return digits.slice(0, 10);
}

function sanitizeLoginMailLocalPart(value) {
  return String(value).split("@")[0].replace(/[^a-zA-Z0-9._-]/g, "");
}

function normalizePakistanPhoneNumber(value) {
  let digits = String(value).replace(/\D/g, "");

  if (digits.startsWith("92")) {
    digits = digits.slice(2);
  }

  if (digits.startsWith("0")) {
    digits = digits.slice(1);
  }

  return digits;
}

function validateEmployeeName(value) {
  const name = normalizeText(value);

  if (!name) {
    return "Employee name is required.";
  }

  if (/\d/.test(name)) {
    return "Employee name cannot contain numbers.";
  }

  if (name.length < 2) {
    return "Employee name must be at least 2 characters.";
  }

  if (!/^[a-zA-Z\s.'-]+$/.test(name)) {
    return "Employee name can only contain letters, spaces, and . ' -";
  }

  return "";
}

function validatePakistanPhoneNumber(value) {
  const digits = normalizePakistanPhoneNumber(value);

  if (!digits) {
    return "Phone number is required.";
  }

  if (digits.length !== 10) {
    return "Phone number must be 10 digits (e.g. 3001234567).";
  }

  if (!/^3\d{9}$/.test(digits)) {
    return "Enter a valid Pakistan mobile number starting with 3.";
  }

  return "";
}

function validateLoginMailLocalPart(value) {
  const localPart = sanitizeLoginMailLocalPart(value);

  if (!localPart) {
    return "Login email is required.";
  }

  if (localPart.length < 3) {
    return "Login email must be at least 3 characters before @.";
  }

  if (!/^[a-zA-Z0-9._-]+$/.test(localPart)) {
    return "Login email can only contain letters, numbers, dots, underscores, and hyphens.";
  }

  return "";
}

function validatePassword(value, required = true) {
  const password = normalizeText(value);

  if (!password) {
    return required ? "Password is required." : "";
  }

  if (password.length < 6) {
    return "Password must be at least 6 characters.";
  }

  return "";
}

function getUserFormErrors(user, options = {}) {
  const { requirePassword = false, validatePasswordIfProvided = false } = options;

  let passwordError = "";

  if (requirePassword) {
    passwordError = validatePassword(user.password, true);
  } else if (validatePasswordIfProvided && normalizeText(user.password)) {
    passwordError = validatePassword(user.password, true);
  }

  return {
    name: validateEmployeeName(user.name),
    number: validatePakistanPhoneNumber(user.number),
    role: user.role ? "" : "Please select a role.",
    gender: user.gender && ["Male", "Female"].includes(user.gender) ? "" : "Please select gender.",
    loginMail: validateLoginMailLocalPart(user.loginMail),
    password: passwordError,
    form: ""
  };
}

function hasUserFormErrors(errors) {
  return Object.values(errors).some(Boolean);
}

function extractLoginMailLocalPart(email) {
  const value = normalizeText(email);

  if (!value || value === "N/A") {
    return "";
  }

  if (value.includes("@")) {
    return value.split("@")[0];
  }

  return value;
}

function buildFullLoginEmail(localPart) {
  return `${sanitizeLoginMailLocalPart(localPart)}${TECGYM_ADMIN_EMAIL_DOMAIN}`;
}

function buildUserPayload(user, options = {}) {
  const number = normalizePakistanPhoneNumber(user.number);
  const payload = {
    name: normalizeText(user.name),
    number,
    phone: number,
    role: user.role,
    gender: user.gender
  };

  if (user.loginMail !== undefined) {
    const loginMail = buildFullLoginEmail(user.loginMail);
    payload.loginId = loginMail;
    payload.loginMail = loginMail;
    payload.email = loginMail;
  }

  const password = normalizeText(user.password);

  if (options.includePassword && password) {
    payload.password = password;
  }

  return payload;
}

function getUserFromResponse(response) {
  const candidates = [
    response?.user,
    response?.tecgymAdminUser,
    response?.adminUser,
    response?.data?.user,
    response?.data?.tecgymAdminUser,
    response?.data?.adminUser,
    response?.data,
    response?.response?.user,
    response?.response?.tecgymAdminUser,
    response?.response?.adminUser,
    response?.response,
    response
  ];

  return candidates.find((candidate) => (
    candidate &&
    !Array.isArray(candidate) &&
    typeof candidate === "object" &&
    (
      getUserId(candidate) ||
      getUserName(candidate) !== "N/A" ||
      getUserLoginMail(candidate) !== "N/A" ||
      getUserPhone(candidate) !== "N/A"
    )
  )) || null;
}

function getRoleDropdownId(role, index) {
  return getRoleId(role) || `${getRoleName(role)}-${index}`;
}

function buildRolePayload(role) {
  return {
    name: role.name.trim(),
    privileges: [...new Set(role.privileges)]
  };
}

function buildUserActivitySnapshot(user) {
  if (!user) {
    return null;
  }

  const loginMail = getUserLoginMail(user);

  return {
    name: getUserName(user) === "N/A" ? "" : getUserName(user),
    number: getUserPhone(user) === "N/A" ? "" : getUserPhone(user),
    role: getUserRole(user) === "N/A" ? "" : getUserRole(user),
    gender: getUserGender(user) === "N/A" ? "" : getUserGender(user),
    email: loginMail === "N/A" ? "" : loginMail,
    loginMail: loginMail === "N/A" ? "" : loginMail
  };
}

function buildUserActivityPayloadValues(previousUser, nextPayload) {
  const previousSnapshot = buildUserActivitySnapshot(previousUser);
  const nextSnapshot = {
    name: nextPayload.name,
    number: nextPayload.number,
    role: nextPayload.role,
    gender: nextPayload.gender,
    email: nextPayload.email || nextPayload.loginMail || nextPayload.loginId || "",
    loginMail: nextPayload.loginMail || nextPayload.email || nextPayload.loginId || ""
  };

  const { previousValue, updatedValue } = buildActivityChangeValues(
    previousSnapshot,
    nextSnapshot,
    ["name", "number", "role", "gender", "email"]
  );

  if (!nextPayload.password) {
    return { previousValue, updatedValue };
  }

  if (!updatedValue) {
    return {
      previousValue: previousSnapshot,
      updatedValue: { password: "[updated]" }
    };
  }

  return {
    previousValue,
    updatedValue: {
      ...updatedValue,
      password: "[updated]"
    }
  };
}

function buildRoleActivitySnapshot(role) {
  return {
    name: getRoleName(role) === "N/A" ? "" : getRoleName(role),
    privileges: getRolePrivileges(role)
  };
}

function buildRoleDescription(privileges) {
  return `${privileges.length} privilege${privileges.length === 1 ? "" : "s"} selected`;
}

function getRoleFromResponse(response) {
  const candidates = [
    response?.role,
    response?.tecgymAdminRole,
    response?.adminRole,
    response?.data?.role,
    response?.data?.tecgymAdminRole,
    response?.data?.adminRole,
    response?.data,
    response?.response?.role,
    response?.response?.tecgymAdminRole,
    response?.response?.adminRole,
    response?.response,
    response
  ];

  return candidates.find((candidate) => (
    candidate &&
    !Array.isArray(candidate) &&
    (
      typeof candidate === "string" ||
      (
        typeof candidate === "object" &&
        (candidate._id || candidate.id || getRoleName(candidate) !== "N/A")
      )
    )
  )) || null;
}

function getRoleName(role) {
  if (typeof role === "string") {
    return role.trim() || "N/A";
  }

  if (!role || typeof role !== "object") {
    return "N/A";
  }

  const roleName = role.name ||
    role.roleName ||
    role.title ||
    role.designation ||
    role.role ||
    role.role_name ||
    role.adminRole ||
    role.adminRoleName ||
    role.admin_role_name ||
    role.tecgymAdminRole ||
    role.tecgymAdminRoleName;

  if (typeof roleName === "string") {
    return roleName.trim() || "N/A";
  }

  if (roleName && typeof roleName === "object") {
    return getRoleName(roleName);
  }

  return "N/A";
}

function getRoleMeta(role) {
  return role?.description || role?.detail || "Role information";
}

function getRolePrivileges(role) {
  const privilegeSources = [
    role?.privileges,
    role?.priviledges,
    role?.permissions,
    role?.permission,
    role?.rolePrivileges,
    role?.role_privileges,
    role?.access,
    role?.rights,
    role?.modules,
    role?.role?.privileges,
    role?.role?.priviledges,
    role?.data?.privileges,
    role?.data?.priviledges
  ];

  return [...new Set(privilegeSources.flatMap(normalizePrivilegeValue).filter(Boolean))];
}

function normalizePrivilegeValue(value) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(normalizePrivilegeValue);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((privilege) => privilege.trim())
      .filter(Boolean)
      .map((privilege) => {
        const normalized = privilege.toLowerCase();
        if (normalized === "database sheets - vendor sheet") {
          return "DataBase Sheets - Vendor";
        }
        if (normalized === "contact us") {
          return "DataBase Sheets - Contact Us";
        }
        if (normalized === "login logs") {
          return "DataBase Sheets - Login Logs";
        }
        return privilege;
      });
  }

  if (typeof value === "object") {
    const privilegeName = value.name ||
      value.title ||
      value.label ||
      value.value ||
      value.privilege ||
      value.priviledge ||
      value.permission ||
      value.module ||
      value.page ||
      value.route;

    return [
      ...(typeof privilegeName === "string"
        ? normalizePrivilegeValue(privilegeName)
        : normalizePrivilegeValue(privilegeName)),
      ...normalizePrivilegeValue(value.privileges),
      ...normalizePrivilegeValue(value.priviledges),
      ...normalizePrivilegeValue(value.permissions),
      ...normalizePrivilegeValue(value.children),
      ...normalizePrivilegeValue(value.subPrivileges)
    ];
  }

  return [];
}

const pageStyle = {
  minHeight: "calc(100vh - 80px)",
  padding: "clamp(16px, 4vw, 40px) clamp(12px, 4vw, 50px)",
  background: "var(--home-page-bg, linear-gradient(135deg, #1e2127 0%, #2a2d35 100%))",
  color: "var(--home-heading, #fff)"
};

const headerCardStyle = {
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "18px",
  padding: "28px",
  marginBottom: "24px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.25)"
};

const eyebrowStyle = {
  margin: "0 0 8px 0",
  color: "#4ade80",
  fontSize: "14px",
  fontWeight: 800,
  letterSpacing: "1px",
  textTransform: "uppercase"
};

const titleStyle = {
  margin: "0 0 10px 0",
  fontSize: "clamp(24px, 5vw, 34px)",
  fontWeight: 800
};

const subtitleStyle = {
  margin: 0,
  color: "var(--home-muted, #888)",
  fontSize: "16px"
};

const tabWrapperStyle = {
  display: "flex",
  gap: "14px",
  flexWrap: "wrap",
  marginBottom: "24px"
};

const tabButtonStyle = {
  border: "1px solid var(--home-border, #444)",
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  color: "var(--home-muted, #ccc)",
  borderRadius: "12px",
  padding: "14px 28px",
  fontSize: "16px",
  fontWeight: 700,
  cursor: "pointer",
  transition: "all 0.2s ease"
};

const activeTabButtonStyle = {
  ...tabButtonStyle,
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  borderColor: "#187718",
  color: "#fff",
  boxShadow: "0 4px 16px rgba(24, 119, 24, 0.4)"
};

const sectionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap",
  marginBottom: "20px"
};

const sectionTitleStyle = {
  margin: "0 0 6px 0",
  fontSize: "24px",
  fontWeight: 800
};

const sectionSubtitleStyle = {
  margin: 0,
  color: "var(--home-muted, #888)",
  fontSize: "14px"
};

const searchInputStyle = {
  width: "min(260px, 100%)",
  maxWidth: "100%",
  boxSizing: "border-box",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "10px",
  backgroundColor: "var(--home-page-bg, #1f232b)",
  color: "var(--home-heading, #fff)",
  padding: "12px 14px",
  fontSize: "14px",
  outline: "none"
};

const userActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  flex: "1 1 220px",
  minWidth: 0
};

const addUserButtonStyle = {
  border: "1px solid #187718",
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "12px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0,
  boxShadow: "0 4px 12px rgba(24, 119, 24, 0.3)"
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  backgroundColor: "rgba(0, 0, 0, 0.65)",
  backdropFilter: "blur(4px)"
};

const modalCardStyle = {
  width: "min(100%, 460px)",
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 24px 60px rgba(0, 0, 0, 0.45)",
  color: "var(--home-heading, #fff)"
};

const modalHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "16px",
  marginBottom: "20px"
};

const modalTitleStyle = {
  margin: "0 0 6px 0",
  fontSize: "24px",
  fontWeight: 800
};

const modalSubtitleStyle = {
  margin: 0,
  color: "var(--home-muted, #888)",
  fontSize: "14px"
};

const closeButtonStyle = {
  border: "1px solid var(--home-border, #444)",
  backgroundColor: "transparent",
  color: "var(--home-heading, #fff)",
  borderRadius: "10px",
  width: "36px",
  height: "36px",
  cursor: "pointer",
  fontWeight: 800
};

const fieldLabelStyle = {
  display: "block",
  marginBottom: "16px",
  color: "var(--home-heading, #fff)",
  fontSize: "14px",
  fontWeight: 700
};

const formInputStyle = {
  display: "block",
  width: "100%",
  marginTop: "8px",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "10px",
  backgroundColor: "var(--home-page-bg, #1f232b)",
  color: "var(--home-heading, #fff)",
  padding: "12px 14px",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box"
};

const genderOptionsStyle = {
  display: "flex",
  gap: "14px",
  flexWrap: "wrap",
  marginTop: "10px"
};

const genderOptionStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "999px",
  padding: "9px 14px",
  backgroundColor: "var(--home-page-bg, #1f232b)",
  color: "var(--home-heading, #fff)",
  cursor: "pointer",
  fontWeight: 700
};

const genderRadioStyle = {
  width: "16px",
  height: "16px",
  accentColor: "#187718"
};

const formErrorStyle = {
  color: "#fca5a5",
  backgroundColor: "rgba(127, 29, 29, 0.25)",
  border: "1px solid rgba(248, 113, 113, 0.45)",
  borderRadius: "10px",
  padding: "10px 12px",
  marginBottom: "16px",
  fontSize: "14px",
  fontWeight: 700
};

const modalActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  marginTop: "8px"
};

const primaryButtonStyle = {
  border: "1px solid #187718",
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(24, 119, 24, 0.3)"
};

const secondaryButtonStyle = {
  border: "1px solid var(--home-border, #444)",
  backgroundColor: "transparent",
  color: "var(--home-heading, #fff)",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer"
};

const detailBackButtonStyle = {
  ...secondaryButtonStyle,
  padding: "9px 14px",
  fontSize: "13px",
  whiteSpace: "nowrap",
  flexShrink: 0
};

const dangerButtonStyle = {
  border: "1px solid #dc2626",
  background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)"
};

const confirmOverlayStyle = {
  ...modalOverlayStyle,
  zIndex: 2100
};

const confirmCardStyle = {
  ...modalCardStyle,
  width: "min(100%, 360px)",
  textAlign: "center"
};

const confirmTitleStyle = {
  margin: "0 0 8px 0",
  fontSize: "22px",
  fontWeight: 800
};

const confirmTextStyle = {
  margin: "0 0 22px 0",
  color: "var(--home-muted, #888)",
  fontSize: "15px"
};

const confirmActionsStyle = {
  display: "flex",
  justifyContent: "center",
  gap: "12px"
};

const userGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 280px), 1fr))",
  gap: "18px"
};

const userCardStyle = {
  border: "1px solid var(--home-border, #444)",
  borderRadius: "12px",
  padding: "16px",
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
  minWidth: 0
};

const compactUserCardHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  minWidth: 0
};

const compactUserSummaryStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "7px",
  minWidth: 0,
  flex: 1
};

const userAvatarStyle = {
  width: "64px",
  height: "64px",
  borderRadius: "50%",
  overflow: "hidden",
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#111827",
  border: "1px solid rgba(147, 197, 253, 0.25)",
  boxShadow: "0 0 0 3px rgba(147, 197, 253, 0.08)"
};

const userDetailAvatarStyle = {
  ...userAvatarStyle,
  width: "112px",
  height: "112px",
  alignSelf: "center"
};

const profileAvatarButtonStyle = {
  border: "none",
  backgroundColor: "transparent",
  padding: 0,
  cursor: "pointer",
  borderRadius: "50%"
};

const profileUploadPreviewStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "20px",
  minHeight: "200px"
};

const profileUploadPreviewImageStyle = {
  width: "180px",
  height: "180px",
  borderRadius: "50%",
  objectFit: "cover",
  border: "2px solid rgba(147, 197, 253, 0.45)",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)"
};

const profileUploadPlaceholderStyle = {
  width: "180px",
  height: "180px",
  borderRadius: "50%",
  overflow: "hidden",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#111827",
  border: "1px solid rgba(147, 197, 253, 0.25)"
};

const profileUploadAvatarIconStyle = {
  transform: "scale(2.2)"
};

const profileUploadActionsStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
  marginTop: "8px"
};

const hiddenFileInputStyle = {
  display: "none"
};

const userAvatarImageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block"
};

const userDetailScreenStyle = {
  padding: "0",
  margin: "0",
  border: "none",
  backgroundColor: "transparent",
  boxShadow: "none",
  maxWidth: "100%"
};

const userDetailProfileStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
  marginBottom: "22px",
  paddingBottom: "22px",
  borderBottom: "1px solid var(--home-border, #444)",
  textAlign: "center"
};

const userDetailNameStyle = {
  margin: 0,
  fontSize: "24px",
  fontWeight: 800,
  color: "var(--home-heading, #fff)",
  wordBreak: "break-word"
};

const userNameValueStyle = {
  fontSize: "16px",
  fontWeight: 800,
  color: "var(--home-heading, #fff)"
};

const compactUserNameStyle = {
  ...userNameValueStyle,
  width: "100%",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  lineHeight: 1.35
};

const userCredentialValueStyle = {
  textAlign: "right",
  wordBreak: "break-word"
};

const userCredentialGroupStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "8px",
  minWidth: 0,
  textAlign: "right"
};

const userCredentialCopyButtonStyle = {
  border: "1px solid rgba(147, 197, 253, 0.35)",
  backgroundColor: "rgba(59, 130, 246, 0.14)",
  color: "#93c5fd",
  borderRadius: "8px",
  width: "28px",
  height: "28px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  cursor: "pointer",
  flexShrink: 0
};

const userDetailsStyle = {
  display: "grid",
  gap: "12px",
  color: "var(--home-heading, #fff)",
  fontSize: "14px"
};

const userDetailRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px"
};

const userCardActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  marginTop: "16px",
  paddingTop: "14px",
  borderTop: "1px solid var(--home-border, #444)"
};

const cardUpdateButtonStyle = {
  ...secondaryButtonStyle,
  padding: "9px 14px",
  fontSize: "13px"
};

const cardDeleteButtonStyle = {
  ...dangerButtonStyle,
  padding: "9px 14px",
  fontSize: "13px"
};

const viewDetailsButtonStyle = {
  ...primaryButtonStyle,
  width: "100%",
  padding: "9px 14px",
  borderRadius: "8px",
  fontSize: "13px",
  boxShadow: "none"
};

const userDetailLabelStyle = {
  color: "var(--home-muted, #888)",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontSize: "12px"
};

const userRoleBadgeStyle = {
  display: "inline-block",
  maxWidth: "100%",
  borderRadius: "6px",
  padding: "4px 9px",
  backgroundColor: "rgba(34, 197, 94, 0.16)",
  border: "1px solid rgba(74, 222, 128, 0.35)",
  color: "#4ade80",
  fontSize: "12px",
  lineHeight: 1.3,
  fontWeight: 700,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const deleteUserDetailsStyle = {
  display: "grid",
  gap: "14px",
  margin: "18px 0 22px 0",
  padding: "16px",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "12px",
  backgroundColor: "var(--home-page-bg, #1f232b)",
  textAlign: "left"
};

const emptyStateStyle = {
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px dashed var(--home-border, #444)",
  borderRadius: "14px",
  color: "var(--home-muted, #aaa)",
  padding: "28px",
  textAlign: "center",
  fontWeight: 600
};

const errorStyle = {
  ...emptyStateStyle,
  color: "#fca5a5",
  borderColor: "rgba(248, 113, 113, 0.45)",
  backgroundColor: "rgba(127, 29, 29, 0.25)"
};

const roleGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))",
  gap: "16px"
};

const roleCardStyle = {
  border: "1px solid var(--home-border, #444)",
  borderRadius: "14px",
  padding: "18px",
  backgroundColor: "var(--home-page-bg, #1f232b)"
};

const roleTitleStyle = {
  margin: "0 0 8px 0",
  fontSize: "18px",
  color: "var(--home-heading, #fff)"
};

const roleMetaStyle = {
  margin: 0,
  color: "var(--home-muted, #888)",
  fontSize: "14px",
  lineHeight: "1.5"
};

const rolePrivilegesDropdownStyle = {
  marginTop: "14px",
  border: "1px solid #d1d5db",
  borderRadius: "12px",
  overflow: "hidden",
  backgroundColor: "#fff"
};

const rolePrivilegesToggleStyle = {
  width: "100%",
  border: "none",
  backgroundColor: "transparent",
  color: "#111",
  padding: "11px 12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 800
};

const rolePrivilegesListStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  padding: "12px",
  borderTop: "1px solid #d1d5db",
  backgroundColor: "#fff"
};

const rolePrivilegeItemStyle = {
  borderRadius: "999px",
  padding: "6px 10px",
  backgroundColor: "#f3f4f6",
  border: "1px solid #d1d5db",
  color: "#111",
  fontSize: "12px",
  fontWeight: 800
};

const roleNoPrivilegesStyle = {
  color: "#555",
  fontSize: "13px",
  fontWeight: 700
};

const privilegeSummaryStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "14px",
  flexWrap: "wrap",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "12px",
  padding: "14px",
  marginBottom: "16px",
  backgroundColor: "var(--home-page-bg, #1f232b)"
};

const privilegeSummaryTitleStyle = {
  margin: "0 0 4px 0",
  color: "var(--home-heading, #fff)",
  fontSize: "14px",
  fontWeight: 800
};

const privilegeSummaryTextStyle = {
  margin: 0,
  color: "var(--home-muted, #888)",
  fontSize: "13px"
};

const privilegeOverlayStyle = {
  ...modalOverlayStyle,
  zIndex: 2200
};

const privilegeCardStyle = {
  ...modalCardStyle,
  width: "min(100%, 760px)"
};

const privilegeListStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
  gap: "12px",
  marginBottom: "18px",
  maxHeight: "58vh",
  overflowY: "auto",
  paddingRight: "4px"
};

const privilegeGroupStyle = {
  border: "1px solid var(--home-border, #444)",
  borderRadius: "12px",
  backgroundColor: "var(--home-page-bg, #1f232b)",
  overflow: "hidden"
};

const privilegeOptionStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  color: "var(--home-heading, #fff)",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer"
};

const privilegeGroupHeaderStyle = {
  ...privilegeOptionStyle,
  padding: "12px",
  backgroundColor: "rgba(24, 119, 24, 0.16)",
  borderBottom: "1px solid var(--home-border, #444)"
};

const privilegeGroupTitleStyle = {
  fontSize: "15px",
  fontWeight: 800
};

const subPrivilegeListStyle = {
  display: "grid",
  gap: "8px",
  padding: "10px 12px 12px 34px"
};

const subPrivilegeItemStyle = {
  display: "grid",
  gap: "8px"
};

const subPrivilegeOptionStyle = {
  ...privilegeOptionStyle,
  color: "var(--home-muted, #bbb)",
  fontSize: "13px",
  fontWeight: 600
};

const nestedSubPrivilegeListStyle = {
  display: "grid",
  gap: "8px",
  marginLeft: "12px",
  paddingLeft: "16px",
  borderLeft: "1px solid var(--home-border, #444)"
};

const nestedSubPrivilegeOptionStyle = {
  ...subPrivilegeOptionStyle,
  color: "var(--home-muted, #aaa)",
  fontSize: "12px"
};

const privilegeCheckboxStyle = {
  width: "16px",
  height: "16px",
  accentColor: "#187718"
};

export default EmployeeManagement;
